import { useState, useEffect } from 'react';
import { Bot, Save, Trash2, Plus, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import API from '../services/api';

function AIAssistants() {
  const [assistants, setAssistants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    model: 'gpt-4o',
    instructions: '',
    temperature: 0.7
  });

  useEffect(() => {
    loadAssistants();
  }, []);

  const loadAssistants = async () => {
    setLoading(true);
    try {
      const response = await API.get('/ai-assistants');
      setAssistants(response.data.data || []);
    } catch (error) {
      toast.error('Failed to load AI Assistants');
    } finally {
      setLoading(false);
    }
  };

  const selectAssistant = async (id) => {
    try {
      const response = await API.get(`/ai-assistants/${id}`);
      const a = response.data.data;
      setSelectedId(id);
      setFormData({
        name: a.name,
        model: a.model,
        instructions: a.instructions,
        temperature: parseFloat(a.temperature)
      });
    } catch (error) {
      toast.error('Failed to load assistant details');
    }
  };

  const startNew = () => {
    setSelectedId(null);
    setFormData({ name: '', model: 'gpt-4o', instructions: '', temperature: 0.7 });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.instructions.trim()) {
      toast.error('Name and Instructions are required');
      return;
    }

    setSaving(true);
    const toastId = toast.loading(selectedId ? 'Updating assistant...' : 'Creating assistant...');

    try {
      if (selectedId) {
        await API.put(`/ai-assistants/${selectedId}`, formData);
        toast.success('AI Assistant updated!', { id: toastId });
      } else {
        const response = await API.post('/ai-assistants', formData);
        toast.success('AI Assistant created!', { id: toastId });
        setSelectedId(response.data.data.id);
      }
      await loadAssistants();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save assistant', { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    if (!window.confirm('Delete this AI Assistant? Any flow node using it will stop working.')) return;

    const toastId = toast.loading('Deleting...');
    try {
      await API.delete(`/ai-assistants/${selectedId}`);
      toast.success('AI Assistant deleted', { id: toastId });
      startNew();
      await loadAssistants();
    } catch (error) {
      toast.error('Failed to delete assistant', { id: toastId });
    }
  };

 if (loading) {
  return (
    <div className="dash-page" style={{ textAlign: 'center' }}>
      <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
    </div>
  );
}

return (
  <div className="dash-page">
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '26px', fontWeight: '800', color: '#1e293b', margin: 0 }}>
            <Bot size={28} /> AI Assistants
          </h1>
          <p style={{ color: '#64748b', marginTop: '6px' }}>
            Create reusable AI assistants and use them inside any Flow via the "AI Assistant" node.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '24px' }}>

          {/* LEFT — LIST */}
          <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <button
              onClick={startNew}
              style={{
                width: '100%', padding: '14px', background: '#1e3a5f', color: 'white',
                border: 'none', fontWeight: '700', fontSize: '14px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
              }}
            >
              <Plus size={16} /> New Assistant
            </button>

            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {assistants.length === 0 ? (
                <div style={{ padding: '30px 20px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
                  No assistants yet. Create your first one.
                </div>
              ) : (
                assistants.map(a => (
                  <div
                    key={a.id}
                    onClick={() => selectAssistant(a.id)}
                    style={{
                      padding: '14px 18px',
                      borderBottom: '1px solid #f1f5f9',
                      cursor: 'pointer',
                      background: selectedId === a.id ? '#eff6ff' : 'white',
                      borderLeft: selectedId === a.id ? '3px solid #1e3a5f' : '3px solid transparent'
                    }}
                  >
                    <div style={{ fontWeight: '700', fontSize: '14px', color: '#1e293b' }}>{a.name}</div>
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>{a.model}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* RIGHT — FORM */}
          <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '28px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px', color: '#1e293b' }}>
              {selectedId ? 'Edit Assistant' : 'Create New Assistant'}
            </h2>

            <form onSubmit={handleSave}>
              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                  Assistant Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Course Advisor"
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
                  required
                />
                <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>Give a recognizable name for your assistant</p>
              </div>

              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>Model</label>
                <select
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px' }}
                >
                  <option value="gpt-4o">GPT-4o — Fast & smart</option>
                  <option value="gpt-4o-mini">GPT-4o Mini — Best value</option>
                  <option value="gpt-4-turbo">GPT-4 Turbo — High quality</option>
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo — Fast & cheap</option>
                </select>
              </div>

              <div style={{ marginBottom: '18px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                  Instructions (Prompt) <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <textarea
                  value={formData.instructions}
                  onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                  rows={8}
                  placeholder="You are a helpful assistant that answers questions about our courses..."
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical' }}
                  required
                />
                <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                  Describe the assistant's business behavior. Do not include flow structure — that's handled automatically.
                </p>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                  Temperature: {formData.temperature}
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={formData.temperature}
                  onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) })}
                  style={{ width: '100%' }}
                />
                <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>Lower = more focused, higher = more creative</p>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '10px 22px', background: '#1e3a5f', color: 'white',
                    border: 'none', borderRadius: '8px', fontWeight: '700',
                    fontSize: '14px', cursor: 'pointer', opacity: saving ? 0.6 : 1
                  }}
                >
                  <Save size={15} /> {selectedId ? 'Update Assistant' : 'Save Assistant'}
                </button>

                {selectedId && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '10px 22px', background: '#fee2e2', color: '#dc2626',
                      border: '1px solid #fecaca', borderRadius: '8px', fontWeight: '700',
                      fontSize: '14px', cursor: 'pointer'
                    }}
                  >
                    <Trash2 size={15} /> Remove
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AIAssistants;